import { type FormEvent, useMemo, useState } from "react";
import {
  type BookingEvent,
  type EventDraft,
  type EventRole,
  type EventRoleDraft,
  type PaymentPolicy,
  saveBookingEvent,
  saveEventRole,
} from "./lib/eventAdminRepository";
import "./event-admin-editor.css";

type Props = {
  events: BookingEvent[];
  onSaved: (message: string) => Promise<void>;
};

type EventForm = {
  id: string;
  title: string;
  eyebrow: string;
  summary: string;
  description: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  venueName: string;
  addressLine: string;
  postalCode: string;
  city: string;
  countryCode: string;
  registrationOpensDate: string;
  registrationOpensTime: string;
  registrationClosesDate: string;
  registrationClosesTime: string;
  status: EventDraft["status"];
  sortOrder: number;
  isActive: boolean;
};

type RoleForm = {
  id: string;
  eventId: string;
  label: string;
  description: string;
  capacity: string;
  price: string;
  currency: string;
  paymentPolicy: PaymentPolicy;
  cancellationTerms: string;
  sortOrder: number;
  isActive: boolean;
};

const EMPTY_EVENT: EventForm = {
  id: "",
  title: "",
  eyebrow: "",
  summary: "",
  description: "",
  startsAt: "",
  endsAt: "",
  timezone: "Europe/Berlin",
  venueName: "",
  addressLine: "",
  postalCode: "",
  city: "Berlin",
  countryCode: "DE",
  registrationOpensDate: "",
  registrationOpensTime: "",
  registrationClosesDate: "",
  registrationClosesTime: "",
  status: "draft",
  sortOrder: 0,
  isActive: true,
};

const EMPTY_ROLE: RoleForm = {
  id: "",
  eventId: "",
  label: "",
  description: "",
  capacity: "",
  price: "",
  currency: "EUR",
  paymentPolicy: "online_or_invoice",
  cancellationTerms: "",
  sortOrder: 0,
  isActive: true,
};

export function EventAdminEditor({ events, onSaved }: Props) {
  const [eventForm, setEventForm] = useState<EventForm>(EMPTY_EVENT);
  const [roleForm, setRoleForm] = useState<RoleForm>(EMPTY_ROLE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedEvent = useMemo(
    () => events.find((event) => event.id === eventForm.id) ?? null,
    [eventForm.id, events],
  );

  function chooseEvent(id: string) {
    if (!id) {
      setEventForm(EMPTY_EVENT);
      setRoleForm(EMPTY_ROLE);
      setError(null);
      return;
    }
    const event = events.find((candidate) => candidate.id === id);
    if (!event) return;
    setEventForm(eventToForm(event));
    setRoleForm({ ...EMPTY_ROLE, eventId: event.id });
    setError(null);
  }

  function chooseRole(id: string) {
    if (!selectedEvent || !id) {
      setRoleForm({ ...EMPTY_ROLE, eventId: selectedEvent?.id ?? eventForm.id });
      return;
    }
    const role = selectedEvent.roles.find((candidate) => candidate.id === id);
    if (role) setRoleForm(roleToForm(role));
  }

  async function saveEvent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const id = await saveBookingEvent(formToEvent(eventForm));
      setEventForm((current) => ({ ...current, id }));
      setRoleForm((current) => ({ ...current, eventId: id }));
      await onSaved(
        eventForm.id
          ? "Die Veranstaltung wurde aktualisiert."
          : "Die Veranstaltung wurde als Entwurf angelegt.",
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Die Veranstaltung konnte nicht gespeichert werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function saveRole(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const eventId = roleForm.eventId || eventForm.id;
    if (!eventId) {
      setError("Bitte zuerst die Veranstaltung speichern.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const id = await saveEventRole(formToRole({ ...roleForm, eventId }));
      setRoleForm((current) => ({ ...current, id, eventId }));
      await onSaved(
        roleForm.id ? "Die Teilnahmeform wurde aktualisiert." : "Die Teilnahmeform wurde angelegt.",
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Die Teilnahmeform konnte nicht gespeichert werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="event-admin-editor" aria-label="Veranstaltung bearbeiten">
      <header className="event-admin-editor__header">
        <div>
          <p className="workspace-kicker">Gestaltung</p>
          <h2>Veranstaltung anlegen und veröffentlichen</h2>
          <p>
            Termin, Ort, Teilnahmeformen, Kapazitäten und Zahlungsweg werden hier gemeinsam
            gepflegt.
          </p>
        </div>
        <label>
          Veranstaltung
          <select value={eventForm.id} onChange={(event) => chooseEvent(event.target.value)}>
            <option value="">Neue Veranstaltung</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.title} · {statusLabel(event.status)}
              </option>
            ))}
          </select>
        </label>
      </header>

      <form className="event-admin-editor__form" onSubmit={saveEvent}>
        <div className="event-admin-editor__section-title">
          <div>
            <p className="workspace-kicker">Grunddaten</p>
            <h3>{eventForm.id ? "Veranstaltung bearbeiten" : "Neue Veranstaltung"}</h3>
          </div>
          <button type="submit" disabled={busy}>
            {busy ? "Wird gespeichert …" : "Veranstaltung speichern"}
          </button>
        </div>

        <div className="event-admin-editor__grid">
          <label>
            Titel
            <input
              required
              value={eventForm.title}
              onChange={(event) => setEventForm({ ...eventForm, title: event.target.value })}
            />
          </label>
          <label>
            Kurze Datumszeile
            <input
              placeholder="12.–16. November 2026"
              value={eventForm.eyebrow}
              onChange={(event) => setEventForm({ ...eventForm, eyebrow: event.target.value })}
            />
          </label>
          <label className="is-wide">
            Kurzbeschreibung
            <textarea
              rows={2}
              value={eventForm.summary}
              onChange={(event) => setEventForm({ ...eventForm, summary: event.target.value })}
            />
          </label>
          <label className="is-wide">
            Ausführliche Beschreibung
            <textarea
              rows={4}
              value={eventForm.description}
              onChange={(event) => setEventForm({ ...eventForm, description: event.target.value })}
            />
          </label>
          <label>
            Beginn
            <input
              required
              type="datetime-local"
              value={eventForm.startsAt}
              onChange={(event) => setEventForm({ ...eventForm, startsAt: event.target.value })}
            />
          </label>
          <label>
            Ende
            <input
              required
              type="datetime-local"
              value={eventForm.endsAt}
              onChange={(event) => setEventForm({ ...eventForm, endsAt: event.target.value })}
            />
          </label>
          <fieldset className="event-admin-editor__datetime-group">
            <legend>Anmeldung öffnet</legend>
            <label>
              Datum
              <input
                type="date"
                value={eventForm.registrationOpensDate}
                onChange={(event) =>
                  setEventForm({ ...eventForm, registrationOpensDate: event.target.value })
                }
              />
            </label>
            <label>
              Uhrzeit
              <input
                type="time"
                value={eventForm.registrationOpensTime}
                onChange={(event) =>
                  setEventForm({ ...eventForm, registrationOpensTime: event.target.value })
                }
              />
            </label>
          </fieldset>
          <fieldset className="event-admin-editor__datetime-group">
            <legend>Anmeldung schließt</legend>
            <label>
              Datum
              <input
                type="date"
                value={eventForm.registrationClosesDate}
                onChange={(event) =>
                  setEventForm({ ...eventForm, registrationClosesDate: event.target.value })
                }
              />
            </label>
            <label>
              Uhrzeit
              <input
                type="time"
                value={eventForm.registrationClosesTime}
                onChange={(event) =>
                  setEventForm({ ...eventForm, registrationClosesTime: event.target.value })
                }
              />
            </label>
          </fieldset>
          <label>
            Veranstaltungsort
            <input
              value={eventForm.venueName}
              onChange={(event) => setEventForm({ ...eventForm, venueName: event.target.value })}
            />
          </label>
          <label>
            Straße und Hausnummer
            <input
              value={eventForm.addressLine}
              onChange={(event) => setEventForm({ ...eventForm, addressLine: event.target.value })}
            />
          </label>
          <label>
            Postleitzahl
            <input
              value={eventForm.postalCode}
              onChange={(event) => setEventForm({ ...eventForm, postalCode: event.target.value })}
            />
          </label>
          <label>
            Stadt
            <input
              value={eventForm.city}
              onChange={(event) => setEventForm({ ...eventForm, city: event.target.value })}
            />
          </label>
          <label>
            Status
            <select
              value={eventForm.status}
              onChange={(event) =>
                setEventForm({ ...eventForm, status: event.target.value as EventDraft["status"] })
              }
            >
              <option value="draft">Entwurf</option>
              <option value="published">Veröffentlicht</option>
              <option value="cancelled">Abgesagt</option>
              <option value="completed">Abgeschlossen</option>
            </select>
          </label>
          <label className="event-admin-editor__checkbox">
            <input
              checked={eventForm.isActive}
              type="checkbox"
              onChange={(event) => setEventForm({ ...eventForm, isActive: event.target.checked })}
            />
            Aktiv in der Verwaltung
          </label>
        </div>
      </form>

      {eventForm.id ? (
        <form className="event-admin-editor__form" onSubmit={saveRole}>
          <div className="event-admin-editor__section-title">
            <div>
              <p className="workspace-kicker">Teilnahme und Zahlung</p>
              <h3>Teilnahmeform</h3>
            </div>
            <label>
              Vorhandene Teilnahmeform
              <select
                value={roleForm.id}
                disabled={!selectedEvent}
                onChange={(event) => chooseRole(event.target.value)}
              >
                <option value="">Neue Teilnahmeform</option>
                {selectedEvent?.roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="event-admin-editor__grid">
            <label>
              Bezeichnung
              <input
                required
                disabled={!eventForm.id}
                value={roleForm.label}
                onChange={(event) => setRoleForm({ ...roleForm, label: event.target.value })}
              />
            </label>
            <label>
              Kapazität
              <input
                min="1"
                type="number"
                disabled={!eventForm.id}
                value={roleForm.capacity}
                onChange={(event) => setRoleForm({ ...roleForm, capacity: event.target.value })}
              />
            </label>
            <label>
              Preis in Euro
              <input
                min="0"
                step="0.01"
                type="number"
                disabled={!eventForm.id}
                value={roleForm.price}
                onChange={(event) => setRoleForm({ ...roleForm, price: event.target.value })}
              />
            </label>
            <label>
              Zahlungsregel
              <select
                disabled={!eventForm.id}
                value={roleForm.paymentPolicy}
                onChange={(event) =>
                  setRoleForm({ ...roleForm, paymentPolicy: event.target.value as PaymentPolicy })
                }
              >
                <option value="online_or_invoice">Onlinezahlung oder Rechnung</option>
                <option value="online_required">Onlinezahlung erforderlich</option>
                <option value="invoice_required">Rechnung erforderlich</option>
                <option value="free">Kostenfrei</option>
              </select>
            </label>
            <label className="is-wide">
              Beschreibung
              <textarea
                rows={2}
                disabled={!eventForm.id}
                value={roleForm.description}
                onChange={(event) => setRoleForm({ ...roleForm, description: event.target.value })}
              />
            </label>
            <label className="is-wide">
              Stornierungsbedingungen
              <textarea
                rows={2}
                disabled={!eventForm.id}
                value={roleForm.cancellationTerms}
                onChange={(event) =>
                  setRoleForm({ ...roleForm, cancellationTerms: event.target.value })
                }
              />
            </label>
            <label className="event-admin-editor__checkbox">
              <input
                checked={roleForm.isActive}
                disabled={!eventForm.id}
                type="checkbox"
                onChange={(event) => setRoleForm({ ...roleForm, isActive: event.target.checked })}
              />
              Buchbar
            </label>
            <button type="submit" disabled={busy || !eventForm.id}>
              {busy ? "Wird gespeichert …" : "Teilnahmeform speichern"}
            </button>
          </div>
        </form>
      ) : (
        <section className="event-admin-editor__next-step" aria-label="Nächster Schritt">
          <div className="event-admin-editor__step-number">2</div>
          <div>
            <p className="workspace-kicker">Danach</p>
            <h3>Teilnahmeform und Preis ergänzen</h3>
            <p>
              Trage zuerst oben Titel, Beginn und Ende ein und speichere die Veranstaltung. Direkt
              danach öffnet sich hier die Eingabe für Teilnahmeform, Kapazität, Preis und
              Zahlungsweg.
            </p>
          </div>
        </section>
      )}

      {error ? (
        <div className="workspace-notice error" role="alert">
          {error}
        </div>
      ) : null}
    </section>
  );
}

function eventToForm(event: BookingEvent): EventForm {
  return {
    id: event.id,
    title: event.title,
    eyebrow: event.eyebrow,
    summary: event.summary,
    description: event.description,
    startsAt: toLocalDateTime(event.startsAt),
    endsAt: toLocalDateTime(event.endsAt),
    timezone: event.timezone,
    venueName: event.venueName,
    addressLine: event.addressLine,
    postalCode: event.postalCode,
    city: event.city,
    countryCode: event.countryCode,
    registrationOpensDate: toLocalDate(event.registrationOpensAt),
    registrationOpensTime: toLocalTime(event.registrationOpensAt),
    registrationClosesDate: toLocalDate(event.registrationClosesAt),
    registrationClosesTime: toLocalTime(event.registrationClosesAt),
    status: event.status,
    sortOrder: event.sortOrder,
    isActive: event.isActive,
  };
}

function roleToForm(role: EventRole): RoleForm {
  return {
    id: role.id,
    eventId: role.eventId,
    label: role.label,
    description: role.description,
    capacity: role.capacity === null ? "" : String(role.capacity),
    price: role.priceCents === null ? "" : (role.priceCents / 100).toFixed(2),
    currency: role.currency,
    paymentPolicy: role.paymentPolicy,
    cancellationTerms: role.cancellationTerms,
    sortOrder: role.sortOrder,
    isActive: role.isActive,
  };
}

function formToEvent(form: EventForm): EventDraft {
  return {
    id: form.id,
    title: form.title.trim(),
    eyebrow: form.eyebrow.trim(),
    summary: form.summary.trim(),
    description: form.description.trim(),
    startsAt: toIso(form.startsAt),
    endsAt: toIso(form.endsAt),
    timezone: form.timezone,
    bookingLocationId: null,
    venueName: form.venueName.trim(),
    addressLine: form.addressLine.trim(),
    postalCode: form.postalCode.trim(),
    city: form.city.trim(),
    countryCode: form.countryCode.trim().toUpperCase() || "DE",
    registrationOpensAt: combineLocalDateAndTime(
      form.registrationOpensDate,
      form.registrationOpensTime,
    ),
    registrationClosesAt: combineLocalDateAndTime(
      form.registrationClosesDate,
      form.registrationClosesTime,
    ),
    status: form.status,
    sortOrder: form.sortOrder,
    isActive: form.isActive,
  };
}

function formToRole(form: RoleForm): EventRoleDraft {
  const price =
    form.price.trim() === "" ? null : Math.round(Number(form.price.replace(",", ".")) * 100);
  return {
    id: form.id,
    eventId: form.eventId,
    label: form.label.trim(),
    description: form.description.trim(),
    capacity: form.capacity.trim() === "" ? null : Number(form.capacity),
    priceCents: Number.isFinite(price) ? price : null,
    currency: form.currency,
    paymentPolicy: price === 0 ? "free" : form.paymentPolicy,
    cancellationTerms: form.cancellationTerms.trim(),
    sortOrder: form.sortOrder,
    isActive: form.isActive,
  };
}

function toLocalDateTime(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function toLocalDate(value: string | null) {
  return toLocalDateTime(value).slice(0, 10);
}

function toLocalTime(value: string | null) {
  return toLocalDateTime(value).slice(11, 16);
}

function combineLocalDateAndTime(date: string, time: string): string | null {
  if (!date) return null;
  return toIso(`${date}T${time || "23:59"}`);
}

function toIso(value: string) {
  return new Date(value).toISOString();
}

function statusLabel(status: EventDraft["status"]) {
  if (status === "published") return "veröffentlicht";
  if (status === "cancelled") return "abgesagt";
  if (status === "completed") return "abgeschlossen";
  return "Entwurf";
}
